/**
 * @fileoverview Modal content generators
 *
 * ⚠️ REFACTORING RECOMMENDED - Consider splitting by domain:
 * - Scan operations (inspect, clear, customer validation)
 * - Logger validation (status checks, failed loggers)
 * - Statistics -- been refactored into ScanStatisticsHelpers.tsx
 * - Error handling (service errors, shipment conflicts)
 * - Generic confirmations (delete, clear actions)
 *
 * All (I think) functions return ModalContent for use with GenericModal component.
 *
 * TODO: Ticket: SS-689
 */

import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import LoggerStatusJsx from '../components/LoggerCheckStatus/LoggerCheckStatus';
import { Logger, LoggerId, LoggerMasterStatus } from '../types/Logger';
import { DeleteItemModalData, ModalContent } from '../types/Modal';
import { LoggerShipmentRelease } from '../types/Scan';
import { formatFlag } from './FormattingHelpers';
import { getTemperatureLoggerStatusMessage } from './LoggerHelpers';

export const getInspectWarningContent = (): ModalContent => ({
    type: 'CONFIRMATION',
    confirmBtnColor: 'YELLOW',
    header: 'Scan contains loggers that need inspection',
    body: <div>Are you sure you want to proceed and complete the scan?</div>
});

export const getLoggersFailedOnComplete = (failedLoggerIds: Array<string>): ModalContent => ({
    type: 'INFORMATION',
    header: 'Completing scan failed!',
    body: failedLoggerIds ? (
        <div className="failed-on-complete-modal-body">
            <div className="margin-right">The following loggers failed validation:</div>
            <div className="flex">
                {failedLoggerIds.map((loggerId) => (
                    <div key={loggerId} data-testid="failed-logger" className="error-text margin-right">
                        {loggerId}
                    </div>
                ))}
            </div>
            <strong>Please remove failed loggers from the list and try again.</strong>
        </div>
    ) : (
        <div>Something went wrong. Please contact customer support</div>
    )
});

export const getFlagCheckDidNotPassContent = (
    logger: Logger
): { status: LoggerMasterStatus | undefined; message: string | undefined } => {
    const flags = logger.status?.flags?.details?.flags ?? [];
    const hasFlags = flags.length > 0;
    const isPassed = logger.status?.flags?.status === 'passed';
    const message = logger.status?.flags?.message;

    if (message?.startsWith('Operational status is ')) {
        return {
            status: logger.status?.flags?.status,
            message: message
        };
    }

    if (isPassed || !hasFlags) {
        return {
            status: logger.status?.flags?.status,
            message: 'No flags to show'
        };
    }

    const highestSeverityFlag = flags.reduce((acc, flag) => {
        if (flag.severity > acc.severity) {
            return flag;
        }

        return acc;
    }, flags[0]);

    return {
        status: logger.status?.flags?.status,
        message: formatFlag(highestSeverityFlag)
    };
};

export const getLoggerDidNotPassContent = (logger: Logger): ModalContent => ({
    type: 'INFORMATION',
    header: `Logger with ID ${logger.loggerId} did not pass!`,
    body: (
        <div className="logger-not-pass-modal-body">
            <div className="error-text">The logger will be removed from the list</div>
            <div className="status-row" role="row">
                <LoggerStatusJsx
                    status={logger.status?.calibration?.status}
                    message={logger.status?.calibration?.message}
                    inModal={true}
                />
                <LoggerStatusJsx
                    status={logger.status?.loggerTest?.status}
                    message={logger.status?.loggerTest?.message}
                    inModal={true}
                />
            </div>
            <div className="status-row" role="row">
                <LoggerStatusJsx
                    status={logger.status?.battery?.status}
                    message={logger.status?.battery?.message}
                    inModal={true}
                />
                <LoggerStatusJsx
                    status={logger.status?.temperature?.status}
                    message={
                        logger.status?.temperature ? getTemperatureLoggerStatusMessage(logger.status?.temperature) : ''
                    }
                    inModal={true}
                />
            </div>
            <div className="status-row" role="row">
                <LoggerStatusJsx {...getFlagCheckDidNotPassContent(logger)} inModal={true} />
                <LoggerStatusJsx
                    status={logger.status?.sensors?.status}
                    message={logger.status?.sensors?.message}
                    inModal={true}
                />
            </div>
            <div className="status-row" role="row">
                <LoggerStatusJsx
                    status={logger.status?.shipment?.status}
                    message={logger.status?.shipment?.message}
                    inModal={true}
                />
                <LoggerStatusJsx
                    status={logger.status?.firmware?.status}
                    message={logger.status?.firmware?.message}
                    inModal={true}
                />
            </div>
        </div>
    )
});

export const getSingleCustomerModalContent = (loggerId?: string, customerName?: string): ModalContent => ({
    type: 'INFORMATION',
    header: 'Logger from another customer scanned',
    body: (
        <div className="single-customer-modal-body">
            <div className="margin-bottom-extra">
                <strong>Outbound scans may not contain loggers from more than one customer.</strong>
            </div>
            <div>
                <span>
                    <strong>Logger ID: </strong>
                    {loggerId ?? 'N/A'}
                </span>
                <span>
                    <strong>Customer: </strong>
                    {customerName ?? 'N/A'}
                </span>
            </div>
        </div>
    )
});

export const getSingleCustomerModalContentOnScanComplete = (customers: Array<string>): ModalContent => ({
    type: 'INFORMATION',
    header: 'Scan contains loggers from more than one customer!',
    body: (
        <div className="single-customer-modal-body">
            <div className="margin-bottom-extra">
                <strong>Outbound scans may not contain loggers from more than one customer.</strong>
            </div>
            <div className="flex-wrap">
                <div className="margin-right">Scan contains loggers from the following customers:</div>
                {customers.map((customer, index) => (
                    <div key={index} className="margin-right">
                        <strong>{customer}</strong>
                    </div>
                ))}
            </div>
        </div>
    )
});

export const getDeleteItemModalContent = (itemToDelete: DeleteItemModalData): ModalContent => ({
    type: 'CONFIRMATION',
    confirmBtnColor: 'YELLOW',
    header: `Delete ${itemToDelete.type.toLowerCase()} with ID ${itemToDelete.id}`,
    body: <strong>Are you sure you want to proceed?</strong>
});

export const getClearScanModalContent = (): ModalContent => ({
    type: 'CONFIRMATION',
    confirmBtnColor: 'YELLOW',
    header: 'Clear scan',
    body: <strong>Are you sure you want to clear the scan?</strong>
});

export const getClearBoxModalContent = (): ModalContent => ({
    type: 'CONFIRMATION',
    confirmBtnColor: 'YELLOW',
    header: 'Clear box',
    body: <strong>Are you sure you want to clear the box?</strong>
});

export const getCheckEndpointServiceErrModalContent = ({
    errorMessage,
    loggerId,
    requestId
}: {
    errorMessage: string | undefined;
    loggerId: LoggerId;
    requestId: string | undefined;
}): ModalContent => {
    return {
        type: 'INFORMATION',
        header: errorMessage ?? 'Unknown error',
        body: (
            <>
                <div className="error-text margin-bottom">
                    {loggerId ? `Scanning logger ${loggerId} failed. ` : ''}
                    {requestId ? ` Request ID: ${requestId}` : ''}
                </div>
                <strong>If the error persists, please contact customer support.</strong>
            </>
        )
    };
};

export const getDevicesUsedInShipmentModalContent = (loggerIds: string[] | undefined): ModalContent => {
    const loggerCount = loggerIds ? loggerIds.length : 0;

    return {
        type: 'INFORMATION',
        header: 'Completing scan failed!',
        body: (
            <div>
                <div>
                    Assigning loggers to a customer is not possible — {loggerCount}
                    {loggerCount === 1 ? ' logger has ' : ' loggers have '} already been used in a shipment:
                </div>
                {loggerCount > 0 ? (
                    <div className="devices-used-in-shipment-modal-body">
                        {loggerIds?.map((loggerId) => (
                            <div key={loggerId}>{loggerId}</div>
                        ))}
                    </div>
                ) : (
                    <div>Something went wrong. Please contact customer support.</div>
                )}
            </div>
        )
    };
};

export const getDevicesReleasedDueToCompletingModalContent = (
    successfullyReleased: Array<LoggerShipmentRelease>,
    failedReleased: Array<LoggerShipmentRelease>,
    isBox: boolean
): ModalContent => ({
    type: 'INFORMATION',
    header: `${isBox ? 'Box' : 'Scan'} completed with some warnings`,
    body: (
        <>
            <div className="margin-bottom-extra">
                The {isBox ? 'box' : 'scan'} was successfully completed, but the following loggers were found in an
                active shipment.
            </div>

            {failedReleased.length > 0 && (
                <div className="margin-bottom-extra">
                    <FontAwesomeIcon icon={['far', 'times-circle']} className="margin-right" id="red" />
                    <strong>Failed to close shipment for IDs: </strong>
                    <span>{failedReleased.map((logger) => logger.loggerId).join(', ')}</span>
                </div>
            )}

            {successfullyReleased.length > 0 && (
                <div>
                    <FontAwesomeIcon icon={['far', 'check-circle']} className="margin-right" id="green" />
                    <strong>Shipment closed for IDs: </strong>
                    <span>{successfullyReleased.map((logger) => logger.loggerId).join(', ')}</span>
                </div>
            )}
        </>
    )
});
